<template>
  <tabs>
    <template #actions>
      <Switch
        v-model="hideDeleted"
        :label="$t('hideDeletedDrivers')"
      />
    </template>
  </tabs>
  <toolbar class="toolbar">
    <filter-pane v-model="filter" :filters="filters" />
  </toolbar>

  <busy-area :busy="loading">
    <main class="px-12 mb-12">
      <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-13">
        <card v-for="chart in chartData" :key="chart.title">
          <span class="chart-title">{{ chart.title }}</span>
          <pie v-if="chart.chartjs?.labels && chart.chartjs?.datasets" :data="chart.chartjs" :options="options" legend="right" />
        </card>
      </div>
    </main>
  </busy-area>
</template>

<script lang="ts" setup>
import Tabs from '@components/structure/navigation/Tabs.vue';
import { Pie } from 'vue-chartjs';
import Card from '@utils/Card.vue';
import { computed, reactive, ref } from 'vue';
import { chunk, groupBy } from 'lodash';
import { useI18n } from 'vue-i18n';
import type {
  Chart, ChartData, ChartOptions, LegendItem,
} from 'chart.js';
import BusyArea from '@utils/busy/BusyArea.vue';
import { getRootCSSVariable } from '@/utils/css';
import { collOptionsProvider, type ProviderParams, useProvider } from '@utils/table/hooks/provider';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import type Filter from '@components/utils/filter/Filter.vue';
import Switch from '@utils/inputs/Switch.vue';
import type { Campaign, DistributionStatistics, Region } from '@/entities/Entities';
import { theme } from '@/utils/tailwind';

const { t } = useI18n();

const filter = ref({});

const hideDeleted = ref(true);

const totalFilter = computed(() => ({ hideDeleted: hideDeleted.value, ...filter.value }));

const filters: Filter[] = [
  {
    key: 'date',
    type: 'datetime',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      label: t('statistics.overview.date'),
    },
  },
  {
    key: 'region',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      options: collOptionsProvider<Region>('/regions', {
        sort: {
          sortKey: 'name',
          sortDirection: 'ASC',
        },
      }, (value) => ({
        label: value.name,
        value: value.id,
      })),
      label: t('statistics.overview.region'),
    },
  },
  {
    key: 'campaignGroup',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      searchable: true,
      minChars: 2,
      filterResults: false,
      clearOnSelect: true,
      delay: 0,
      createOption: false,
      resolveOnLoad: true,
      noOptionsText: t('inputSearchString'),
      options: (query: string, select: any) => {
        const input = query || select.modelValue;
        if (!input) return Promise.resolve([]);
        const filterQuery: ProviderParams = {
          filter: query ? { name: input } : { id: select.modelValue },
        };
        return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
          label: value.name,
          value: value.id,
        }))();
      },
      label: t('statistics.overview.campaignGroup'),
    },
  },
  {
    key: 'profileComplete',
    type: 'bool',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      label: t('driver.profileComplete'),
    },
  },
  {
    key: 'hasBox',
    type: 'bool',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      label: t('driver.hasBox.title'),
      labelTrue: t('driver.hasBox.true'),
      labelFalse: t('driver.hasBox.false'),
    },
  },
];

const providerParams = reactive({
  filter: totalFilter,
});
const { data: statistics, loading } = useProvider<DistributionStatistics>('/statistics/distribution', providerParams);

type Color = [number, number, number];
const hexToRgb = (hex: string) =>
  chunk(hex.slice(1).split(''), 2).map((hexPart) => parseInt(hexPart.join(''), 16)) as Color;
const rgbToHex = (rgb: Color) => `#${rgb.map((v) => v.toString(16)).join('')}`;

const startColor = hexToRgb(getRootCSSVariable(theme().colors.red[120]));
const endColor = hexToRgb(getRootCSSVariable(theme().colors.yellow[100]));

const interpolateColors =
  ([rf, gf, bf]: Color, [rt, gt, bt]: Color) =>
    (percent: number) =>
    [
      Math.round(rf + percent * (rt - rf)),
      Math.round(gf + percent * (gt - gf)),
      Math.round(bf + percent * (bt - bf)),
    ] as Color;

const interpolateRedSpectrum = interpolateColors(startColor, endColor);

const buildGradient = (num: number) =>
  Array.from({ length: num }, (_, i) => rgbToHex(interpolateRedSpectrum(i / num)));

const driversPerAge = computed(() => {
  if (!statistics?.value?.driversPerAge) return [];

  const groups = groupBy(statistics.value.driversPerAge, (entry) => {
    if (entry.age === null) return t('pages.dashboard.distribution.unknown');
    if (entry.age < 16) return '0 - 16';
    if (entry.age < 20) return '16 - 20';
    const base = Math.floor(entry.age / 10) * 10;
    return `${base} - ${base + 10}`;
  });
  return Object.entries(groups).map(([group, values]) => ({
    label: group,
    amountOfDrivers: values.reduce((total, { amountOfDrivers }) => total + amountOfDrivers, 0),
  }));
});

interface PieChart {
  title: string;
  chartjs: ChartData<'pie'>;
}

const chartData = computed(() => {
  if (!statistics?.value) return [];

  return [
    {
      title: t('pages.dashboard.distribution.driversPerRegion'),
      chartjs: {
        labels: statistics.value.driversPerRegion?.map(({ region }) => region.name) ?? [],
        datasets: [
          {
            backgroundColor: buildGradient(statistics.value.driversPerRegion?.length ?? 0),
            data: statistics.value.driversPerRegion?.map(({ amountOfDrivers }) => amountOfDrivers) ?? [],
          },
        ],
      },
    },
    {
      title: t('pages.dashboard.distribution.driversPerGender'),
      chartjs: {
        labels: statistics.value.driversPerGender?.map(({ salutation }) =>
          (salutation ? t(`title.${salutation}`) : t('unknown'))) ?? [],
        datasets: [
          {
            backgroundColor: buildGradient(statistics.value.driversPerGender?.length ?? 0),
            data: statistics.value.driversPerGender?.map(({ amountOfDrivers }) => amountOfDrivers) ?? [],
          },
        ],
      },
    },
    {
      title: t('pages.dashboard.distribution.driversPerAge'),
      chartjs: {
        labels: driversPerAge.value?.map(({ label }) => label) ?? [],
        datasets: [
          {
            backgroundColor: buildGradient(driversPerAge.value?.length ?? 0),
            data: driversPerAge.value?.map(({ amountOfDrivers }) => amountOfDrivers) ?? [],
          },
        ],
      },
    },
    {
      title: t('pages.dashboard.distribution.driversPerBicycleType'),
      chartjs: {
        labels: statistics.value.driversPerBicycleType?.map(
          ({ bicycleType }) => t(`driver.bicycleTypes.${bicycleType}`),
        ) ?? [],
        datasets: [
          {
            backgroundColor: buildGradient(statistics.value.driversPerBicycleType?.length ?? 0),
            data: statistics.value.driversPerBicycleType?.map(
              ({ amountOfDrivers }) => amountOfDrivers,
            ) ?? [],
          },
        ],
      },
    },
    {
      title: t('pages.dashboard.distribution.averageKmPerWeek'),
      chartjs: {
        labels: statistics.value.averageDistancePerWeek?.map(
          ({ range }) => range ?? t('unknown'),
        ) ?? [],
        datasets: [
          {
            backgroundColor: buildGradient(statistics.value.averageDistancePerWeek?.length ?? 0),
            data: statistics.value.averageDistancePerWeek?.map(
              ({ count }) => count,
            ) ?? [],
          },
        ],
      },
    },
    {
      title: t('pages.dashboard.distribution.averageToursPerWeek'),
      chartjs: {
        labels: statistics.value.averageAmountOfToursPerWeek?.map(
          ({ range }) => range ?? t('unknown'),
        ) ?? [],
        datasets: [
          {
            backgroundColor: buildGradient(statistics.value.averageAmountOfToursPerWeek?.length ?? 0),
            data: statistics.value.averageAmountOfToursPerWeek?.map(
              ({ count }) => count,
            ) ?? [],
          },
        ],
      },
    },
  ] as PieChart[];
});

const options: ChartOptions = {
  plugins: {
    legend: {
      position: 'right',
      labels: {
        padding: 20,
        boxWidth: 10,
        boxHeight: 10,
        font: {
          weight: 'bold',
        },
        generateLabels: (chart: Chart): LegendItem[] => {
          const { datasets } = chart.data;
          return datasets[0].data.map((data, i) => ({
            text: [chart.data.labels?.[i], data] as unknown as string, // Just satisfy typescript... types of chartjs are wrong
            lineWidth: 0,
            fillStyle: (datasets[0].backgroundColor as string[])?.[i],
          }));
        },
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-title {
  @apply text-pre-title-big;
}
</style>
